import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import { LoadingSkeleton } from "./loading";

const tableVariants = cva(
  "w-full caption-bottom text-sm",
  {
    variants: {
      variant: {
        default: "border-collapse",
        striped: "border-collapse [&_tbody_tr:nth-child(odd)]:bg-muted/50",
        bordered: "border-collapse border border-border",
        elevated: "border-collapse shadow-sm rounded-lg overflow-hidden",
      },
      size: {
        default: "",
        sm: "text-xs",
        lg: "text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface EnhancedTableProps
  extends React.TableHTMLAttributes<HTMLTableElement>,
    VariantProps<typeof tableVariants> {
  loading?: boolean;
  loadingRows?: number;
  loadingCols?: number;
  emptyMessage?: string;
  stickyHeader?: boolean;
}

export interface TableHeaderProps extends React.HTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: () => void;
}

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  interactive?: boolean;
  selected?: boolean;
}

const EnhancedTable = React.forwardRef<HTMLTableElement, EnhancedTableProps>(
  ({ 
    className, 
    variant, 
    size, 
    loading = false, 
    loadingRows = 5, 
    loadingCols = 4,
    stickyHeader = false,
    children,
    ...props 
  }, ref) => {
    if (loading) {
      return (
        <div className="relative w-full overflow-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent hover:scrollbar-thumb-slate-400 scroll-smooth">
          <table
            ref={ref}
            className={cn(tableVariants({ variant, size, className }))}
            {...props}
          >
            <TableHeader className={stickyHeader ? "sticky top-0 z-10 bg-background" : ""}>
              <TableRow>
                {Array.from({ length: loadingCols }, (_, i) => (
                  <TableHead key={i}>
                    <LoadingSkeleton className="h-4 w-20" />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: loadingRows }, (_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {Array.from({ length: loadingCols }, (_, colIndex) => (
                    <TableCell key={colIndex}>
                      <LoadingSkeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </table>
        </div>
      );
    }

    return (
      <div className="relative w-full overflow-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent hover:scrollbar-thumb-slate-400 scroll-smooth">
        <table
          ref={ref}
          className={cn(tableVariants({ variant, size, className }))}
          {...props}
        >
          {children}
        </table>
      </div>
    );
  }
);
EnhancedTable.displayName = "EnhancedTable";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, interactive = false, selected = false, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b transition-colors",
        interactive && "hover:bg-muted/50 cursor-pointer",
        selected && "bg-muted",
        "data-[state=selected]:bg-muted",
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeaderProps>(
  ({ className, sortable = false, sortDirection = null, onSort, children, ...props }, ref) => {
    const handleSort = () => {
      if (sortable && onSort) {
        onSort();
      }
    };

    return (
      <th
        ref={ref}
        className={cn(
          "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
          sortable && "cursor-pointer hover:text-foreground select-none",
          className
        )}
        onClick={handleSort}
        {...props}
      >
        <div className="flex items-center gap-2">
          {children}
          {sortable && (
            <div className="ml-auto">
              {sortDirection === 'asc' && <ChevronUp className="h-4 w-4" />}
              {sortDirection === 'desc' && <ChevronDown className="h-4 w-4" />}
              {sortDirection === null && <ChevronsUpDown className="h-4 w-4 opacity-50" />}
            </div>
          )}
        </div>
      </th>
    );
  }
);
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-4 align-middle [&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

// Enhanced table container with search and actions
const TableContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    title?: string;
    description?: string;
    actions?: React.ReactNode;
  }
>(({ className, title, description, actions, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("space-y-4", className)}
    {...props}
  >
    {(title || description || actions) && (
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          {title && (
            <h3 className="text-lg font-semibold">{title}</h3>
          )}
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2">
            {actions}
          </div>
        )}
      </div>
    )}
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md">
      {children}
    </div>
  </div>
));
TableContainer.displayName = "TableContainer";

// Empty state component
const TableEmptyState = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    icon?: React.ReactNode;
    message?: string;
    action?: React.ReactNode;
  }
>(({ className, icon, message = "No data available", action, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col items-center justify-center py-12 text-center",
      className
    )}
    {...props}
  >
    {icon && (
      <div className="mb-4 rounded-full bg-muted p-3">
        {icon}
      </div>
    )}
    <p className="text-sm text-muted-foreground mb-4">{message}</p>
    {action && action}
  </div>
));
TableEmptyState.displayName = "TableEmptyState";

export {
  EnhancedTable,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableContainer,
  TableEmptyState,
  tableVariants,
};
